import useCheckSetInput from 'lib/hooks/useCheckSetInput';
import useDateInput from 'lib/hooks/useDateInput';
import useInput from 'lib/hooks/useInput';
import { useDidUpdateEffect } from 'lib/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { ProjectActions, UtilActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
// import { useShallowAppSelector } from 'store/hooks';
// import ProjectListByTable from 'components/project/ProjectListByTable';
import ProjectList from 'components/project/clinic/ProjectList';
import { projectProcessFlagList } from 'lib/mapper';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import { isClinicUserSelector } from 'store/modules/user';
import { useCreateProjectInfoStore } from 'store/local/projectCreateinfo';
import PlainModal from 'components/common/modal/PlainModal';
import CreateProjectInfo from 'components/project/clinic/CreateProjectInfo';

export default function ProjectListContainer({
  patientCode = null,
  listCount = 8,
  isHeader = true,
}: {
  patientCode: string | null;
  listCount?: number;
  isHeader?: boolean;
}) {
  const {
    newNotificationData: { newNotificationList },
    projectsData: { list: projects, pagingData: projectsPagingData },
    projectsSuccess,
    projectsFailure,
    deleteProjectSuccess,
    isClinicUser,
    networkStatus,
    loadedSource,
  } = useShallowAppSelector(state => ({
    newNotificationData: state.notification.newNotifications.data || {},
    projectsData: state.project.projects.data || {},
    projectsSuccess: state.project.projects.success,
    projectsFailure: state.project.projects.failure,
    deleteProjectSuccess: state.project.deleteProject.success,
    isClinicUser: isClinicUserSelector(state),
    networkStatus: state.app.networkStatus,
    loadedSource: state.app.loadedSource,
  }));

  // SECTION: declare local store
  const { isInfoOpen, setIsInfoOpen } = useCreateProjectInfoStore();

  const isOffline = useMemo(() => {
    return loadedSource === 'local';
  }, [loadedSource]);
  const [isLoadSuccess, setIsLoadSuccess] = useState<boolean>(false);
  const [isLoadFailure, setIsLoadFailure] = useState<boolean>(false);

  const page = useInput(1);
  const period = useDateInput(null);
  const checkedProjectProcess = useCheckSetInput(
    isClinicUser ? new Set([-1, 1, 2, 3, 5, 6, 7]) : new Set([-1, 1, 2, 3, 5, 6, 7, -2]),
  );
  // 0, 1, 2, 3, 4, 5, 6, 7
  // new Set(['new', 'preparation', 'scan', 'cad', 'cam', 'milling', 'post', 'completed']),
  const keyword = useInput('');
  const [isSubmit, setIsSubmit] = useState<boolean | null>(null);
  const { ipcRenderer } = window;
  const history = useHistory();
  const { search } = useLocation();
  const { pathname } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [searchParamsDatas, setSearchParamsDatas] = useState<{
    page: number;
    stages?: string;
    keyword?: string;
    patientCode?: string;
    startDate?: string;
    endDate?: string;
  } | null>(null);
  const [isEmptyProject, setIsEmptyProject] = useState(false);

  const handleChangePage = (value: number) => {
    // get project list
    page.setValue(value);
    history.push(
      `${pathname}?page=${value}&keyword=${keyword.value}&stages=${[
        ...checkedProjectProcess.value,
      ].join('%')}&startDate=${!!period?.value ? period?.value[0]?.unix() : ''}&endDate=${
        !!period?.value ? period?.value[1]?.unix() : ''
      }`,
    );
  };

  const handleSearch = () => {
    page.setValue(1);
    history.push(
      `${pathname}?page=1&keyword=${keyword.value}&stages=${[...checkedProjectProcess.value].join(
        '%',
      )}&startDate=${!!period?.value ? period?.value[0]?.unix() : ''}&endDate=${
        !!period?.value ? period?.value[1]?.unix() : ''
      }`,
    );
  };

  const handleDeleteProject = (projectCode: string) => {
    ProjectActions.delete_project_request({ projectCode });
  };

  const handleRefreshList = () => {
    setSearchParamsDatas({
      ...searchParamsDatas,
      page: 1,
      keyword: keyword.value,
      patientCode: patientCode || '',
    });
    setIsLoadSuccess(false);
    setIsLoadFailure(false);
  };

  const handleChangeProjectProcess = (id: number) => {
    let stagesItem: Set<number> = new Set([...checkedProjectProcess.value]);
    checkedProjectProcess.onChange({ value: id });

    if (stagesItem.has(id)) {
      stagesItem.delete(id);
    } else {
      stagesItem.add(id);
    }

    history.push(
      `${pathname}?page=1&keyword=${keyword.value}&stages=${[...stagesItem].join('%')}&startDate=${
        !!period?.value ? period?.value[0]?.unix() : ''
      }&endDate=${!!period?.value ? period?.value[1]?.unix() : ''}`,
    );
  };

  const handleCheckAllStage = (checkAll: boolean) => {
    checkedProjectProcess.setValue(new Set([]));
    let stagesItem: Set<number> = new Set([]);
    if (!checkAll) {
      projectProcessFlagList.forEach((item: any) => {
        if (isClinicUser && item.id === -2) return; // urgent
        if (item.id === -3) return; // none
        if (item.id === 0) return; // new
        if (item.id === 4) return; // cam
        checkedProjectProcess.onChange({ value: item.id });
        stagesItem.add(item.id);
      });
    }

    history.push(
      `${pathname}?page=1&keyword=${keyword.value}&stages=${[...stagesItem].join('%')}&startDate=${
        !!period?.value ? period?.value[0]?.unix() : ''
      }&endDate=${!!period?.value ? period?.value[1]?.unix() : ''}`,
    );
  };

  const handleChangeDate = (e: any) => {
    period.onChange(e);

    history.push(
      `${pathname}?page=${page.value}&keyword=${keyword.value}&stages=${[
        ...checkedProjectProcess.value,
      ].join('%')}&startDate=${!!e ? e[0]?.unix() : ''}&endDate=${!!e ? e[1]?.unix() : ''}`,
    );
  };

  const handleCloseInfo = () => {
    setIsInfoOpen(false);
  };

  const handleCreateProject = () => {
    ProjectActions.init_project_request();
    setIsInfoOpen(true);
  };

  // 최초 진입
  useEffect(() => {
    if (isOffline) {
      UtilActions.fetch_teeth_indication_info_request({
        channel: 'getIndicationInfoList',
        data: {},
      });
    } else {
      UtilActions.fetch_teeth_indication_info_request();
    }

    setSearchParamsDatas({
      // stages: [...checkedProjectProcess.value].join('%'),
      stages: !!searchParams.get('stages')
        ? String(searchParams.get('stages'))
        : [...checkedProjectProcess.value].join('%'),
      page: Number(searchParams.get('page')),
      keyword: String(searchParams.get('keyword')),
      patientCode: patientCode || '',
    });
  }, []);

  // useEffect(() => {
  //   if (!!patientInfo) {
  //     const listText = `${!!patientInfo?.firstName ? patientInfo.firstName : ''} ${
  //       !!patientInfo?.lastName ? patientInfo.lastName : ''
  //     }${!!patientInfo?.identification ? patientInfo.identification : ''}, ${
  //       genderList[patientInfo?.gender as keyof typeof genderList]
  //     }${moment(patientInfo.birthday).isValid() ? `, ${patientInfo?.birthday}` : ''}`;

  //     PatientActions.set_current_patient({
  //       patientCodeIdx: patientInfo?.patientCodeIdx,
  //       patientName: listText,
  //     });
  //   }
  // }, [patientInfo]);

  // SECTION: DidUpdate
  useDidUpdateEffect(() => {
    if (deleteProjectSuccess) {
      setSearchParamsDatas({
        ...searchParamsDatas,
        keyword: keyword.value,
        page: page.value,
        patientCode: patientCode || '',
      });
    }

    // if (!!ipcRenderer) {
    //   ipcRenderer.send('setLatestOnlineProjectList', {});
    // }
  }, [deleteProjectSuccess]);

  useDidUpdateEffect(() => {
    if (!projectsSuccess) return;
    setIsLoadSuccess(true);
    if (projects.length === 0 && page.value !== 1) {
      const changePage = page.value - 1;
      page.setValue(changePage);
      history.replace(
        `${pathname}?page=${changePage}&keyword=${keyword.value}&stages=${[
          ...checkedProjectProcess.value,
        ].join('%')}&startDate=${!!period?.value ? period?.value[0]?.unix() : ''}&endDate=${
          !!period?.value ? period?.value[1]?.unix() : ''
        }`,
      );
    }
  }, [projectsSuccess, projects, page.value]);

  useDidUpdateEffect(() => {
    if (!projectsFailure) return;
    setIsLoadFailure(true);
  }, [projectsFailure]);

  useEffect(() => {
    return () => {
      ProjectActions.fetch_projects_clear();
      setIsLoadSuccess(false);
      setIsLoadFailure(false);
    };
  }, []);

  useEffect(() => {
    if (!checkedProjectProcess.value.size) {
      ProjectActions.fetch_projects_clear();
      setIsEmptyProject(true);
    } else {
      setIsEmptyProject(false);
    }
  }, [checkedProjectProcess.value]);

  useDidUpdateEffect(() => {
    if (isEmptyProject) return;

    if (isOffline) {
      ProjectActions.fetch_projects_request({
        channel: 'getProjectList',
        data: {
          ...searchParamsDatas,
          keyword: keyword.value,
          page: page.value,
          patientCode: patientCode,
          limitCount: listCount,
          startDate: period.value ? period.value[0].unix() : null,
          endDate: period.value ? period.value[1].unix() : null,
        },
      });
    } else {
      ProjectActions.fetch_projects_request({
        ...searchParamsDatas,
        keyword: keyword.value,
        page: page.value,
        patientCode: patientCode,
        limitCount: listCount,
        startDate: period.value ? period.value[0].unix() : null,
        endDate: period.value ? period.value[1].unix() : null,
      });
    }
    setIsLoadSuccess(false);
  }, [searchParamsDatas, isEmptyProject, isOffline]);

  useEffect(() => {
    const stagesParam = !!searchParams.get('stages')
      ? searchParams.get('stages')
      : [...checkedProjectProcess.value].join('%');
    const stagesValue = !!stagesParam
      ? stagesParam?.split('%')?.map((item: string) => {
          return Number(item);
        })
      : [...checkedProjectProcess.value];
    const pageParam = !!searchParams.get('page') ? Number(searchParams.get('page')) : 1;
    const keywordParam = !!searchParams.get('keyword') ? String(searchParams.get('keyword')) : '';
    const startDate = !!searchParams.get('startDate')
      ? Number(searchParams.get('startDate'))
      : null;
    const endDate = !!searchParams.get('endDate') ? Number(searchParams.get('endDate')) : null;

    page.setValue(pageParam);
    keyword.setValue(keywordParam);
    checkedProjectProcess.setValue(new Set(stagesValue));
    period.setValue(
      !!startDate && !!endDate ? [moment.unix(startDate), moment.unix(endDate)] : null,
    );

    setSearchParamsDatas({
      ...searchParamsDatas,
      page: pageParam,
      keyword: keywordParam,
      stages: stagesParam || '',
      startDate: String(startDate) || '',
      endDate: String(endDate) || '',
    });
  }, [searchParams]);

  // 파트너 소켓 수신 시 새로고침
  useDidUpdateEffect(() => {
    if (
      newNotificationList.some((notification: any) => notification.category === 'PROJECT') &&
      page.value === 1
    ) {
      handleRefreshList();
    }
  }, [newNotificationList]);

  return (
    <>
      <ProjectList
        isLoadSuccess={isLoadSuccess}
        isLoadFailure={isLoadFailure}
        projects={!checkedProjectProcess.value.size ? null : projects}
        projectsPagingData={!checkedProjectProcess.value.size ? null : projectsPagingData}
        page={page}
        period={period}
        checkedProjectProcess={checkedProjectProcess}
        keyword={keyword}
        listCount={listCount}
        isHeader={isHeader}
        isOffline={isOffline}
        onChangePage={handleChangePage}
        onSearch={handleSearch}
        onDeleteProject={handleDeleteProject}
        onRefreshList={handleRefreshList}
        onChangeProjectProcess={handleChangeProjectProcess}
        onCheckAllStage={handleCheckAllStage}
        onChangeDate={handleChangeDate}
        onCreateProject={handleCreateProject}
        patientCode={patientCode}
      />
      <PlainModal isOpen={isInfoOpen} onClose={handleCloseInfo} width={660}>
        <CreateProjectInfo from={!!patientCode ? 'patient' : 'create'} />
      </PlainModal>
    </>
  );
}
